import React, { Fragment } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import keyboardKey from 'keyboard-key';
import get from 'lodash/get';
import Checkbox from '../../../components/Checkbox';
import Popup from '../../../components/Popup';
import LocationInput from './location-input';
import LocationList from './location-list';
import LocationEdit from './location-edit';
import Message from '../../../components/Message';
import messages from '../messages';

import '../styles.css';

export class CollegeEventLocationComponent extends React.PureComponent {
  static propTypes = {
    'id': PropTypes.string,
    'aria-labelledby': PropTypes.string,
    'selectedLocation': PropTypes.object,
    'viewer': PropTypes.object,
    'virtual': PropTypes.bool,
    'setVirtual': PropTypes.func.isRequired,
    'onSelectionChange': PropTypes.func.isRequired,
    'createCollegeLocation': PropTypes.func.isRequired,
    'updateCollegeLocation': PropTypes.func.isRequired,
    'deleteCollegeLocation': PropTypes.func.isRequired,
    'enabledFeatures': PropTypes.array.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {

      locations: this.props.viewer.eventLocations,

      showingLocations: false,
      showingLocationEdit: false,
      editLocation: null,
      locationsFocusLock: true,

      isLoading: false,
      requestError: null,
      requestFailed: false,

    };

    this.locationInputReference = null;
  }

  onLocationInputChanged = (input) => {
    const { locations } = this.state;
    const filteredLocations = locations.filter((location) => {
      const name = location.name.toLowerCase();
      return name.includes(input.toLowerCase());
    });
    this.setState({ showingLocations: false }, () => {
      this.setState({ showingLocations: true, filteredLocations });
    });
  }

  onLocationInputKeyDown = (keyCode) => {
    if (keyCode === keyboardKey.Tab) {
      this.setState({ showingLocations: false });
      return;
    }

    if ((keyCode === keyboardKey.ArrowUp || keyCode === keyboardKey.ArrowDown) && this.state.showingLocations) {
      const query = '#locationsPopup ul#location-list li:nth-child(1) > button';
      document.querySelector(query).focus();
      document.querySelector(query).scrollIntoView();
      this.setState({ locationsFocusLock: false });
    }
  }

  setLocationInputReference = (reference) => {
    this.locationInputReference = reference;
  }

  clearLocationEdit = () => {
    this.clearRequestError();
    this.setState({
      editLocation: null,
      showingLocationEdit: false,
      showingLocations: true,
    });
  }

  setLocationEdit = (location) => {
    this.clearRequestError();
    this.setState({
      editLocation: location,
      showingLocationEdit: true,
      showingLocations: false,
    });
  }

  onLocationSelected = (location) => {
    this.clearRequestError();
    this.props.onSelectionChange(null);
    setTimeout(() => this.props.onSelectionChange(location));
    this.setState({
      showingLocations: false,
      showingLocationEdit: false,
      editLocation: null,
      locationsFocusLock: true,
    });
  }

  saveLocation = (location) => {
    this.setState({ isLoading: true });

    const variables = {
      input: {
        collegeEventLocation: {
          name: location.name,
          street: location.street,
          city: location.city,
          state: location.state,
          postalCode: location.postalCode,
        },
      },
    };

    if (location.id) {
      variables.input.collegeEventLocation.id = location.id;
      variables.input.collegeEventLocation.version = location.version;
      this.props.updateCollegeLocation({ variables })
        .then(this.onSaveSuccess, this.onSaveError)
        .catch(this.onSaveError);
    } else {
      variables.input.collegeEventLocation.visible = !!location.shouldSaveLocation;
      this.props.createCollegeLocation({ variables })
        .then(this.onSaveSuccess, this.onSaveError)
        .catch(this.onSaveError);
    }
  }

  onSaveSuccess = (value) => {
    const response = value.data.createCollegeEventLocation || value.data.replaceCollegeEventLocation;
    const locations = response.viewer.eventLocations;
    const createdLocation = (value.data.createCollegeEventLocation || {}).collegeEventLocation;
    const updatedLocation = (value.data.replaceCollegeEventLocation || {}).newCollegeEventLocation;
    const location = createdLocation || updatedLocation;
    this.props.onSelectionChange(location);
    this.props.setVirtual(false);
    this.setState({
      locations,
      isLoading: false,
      showingLocations: false,
      showingLocationEdit: false,
      editLocation: null,
      locationsFocusLock: true,
      requestFailed: false,
      requestError: null,
    });
  };

  onSaveError = (err) => {
    const errorMessage = get(err, 'graphQLErrors[0].message') || get(err, 'message') || '';
    const message = errorMessage.includes('Location name should be unique') ? messages.errorUniqueName : messages.error;
    const requestError = (
      <Message error>
        <FormattedMessage {...message} />
      </Message>
    );
    this.setState({ isLoading: false, requestFailed: true, requestError });
  };

  deleteLocation = (location) => {
    this.setState({ isLoading: true });
    const variables = {
      input: {
        collegeEventLocation: {
          id: location.id,
        },
      },
    };
    this.props.deleteCollegeLocation({ variables })
      .then(this.onDeleteSuccess, this.onDeleteError)
      .catch(this.onDeleteError);
  }

  onDeleteSuccess = (value) => {
    const locations = value.data.deleteCollegeEventLocation.viewer.eventLocations;
    this.props.onSelectionChange(null);
    this.setState({
      locations,
      isLoading: false,
      showingLocations: false,
      showingLocationEdit: false,
      editLocation: null,
      locationsFocusLock: true,
      requestFailed: false,
      requestError: null,
    });
  }

  onDeleteError = () => {
    const requestError = (
      <Message error>
        <FormattedMessage {...messages.deleteError} />
      </Message>
    );
    this.setState({ isLoading: false, requestFailed: true, requestError });
  }

  clearRequestError = () => {
    this.setState({
      requestError: null,
      requestFailed: false,
    });
  }

  onClosePopup = () => {
    this.clearRequestError();
    this.setState({
      showingLocations: false,
      showingLocationEdit: false,
      locationsFocusLock: true,
    });
  }

  renderLocationInput = () => (
    <LocationInput
      inputId={this.props.id}
      accessibilityLabel={this.props['aria-labelledby']}
      selectedLocation={this.props.selectedLocation}
      virtual={this.props.virtual}
      setLocationInputReference={this.setLocationInputReference}
      onLocationInputChanged={this.onLocationInputChanged}
      onLocationInputKeyDown={this.onLocationInputKeyDown}
    />
  );

  renderLocations = () => {
    if (!this.state.showingLocations) return null;
    return (
      <LocationList
        locations={this.state.filteredLocations}
        focusLock={this.state.locationsFocusLock}
        setLocationEdit={this.setLocationEdit}
        onLocationSelected={this.onLocationSelected}
        onClosePopup={this.onClosePopup}
      />
    );
  }

  renderLocationEdit = () => {
    if (!this.state.showingLocationEdit) return null;
    return (
      <LocationEdit
        location={this.state.editLocation}
        isLoading={this.state.isLoading}
        requestError={this.state.requestFailed && this.state.requestError}
        clearRequestError={this.clearRequestError}
        showLocations={this.clearLocationEdit}
        onClosePopup={this.onClosePopup}
        saveLocation={this.saveLocation}
        deleteLocation={this.deleteLocation}
        enabledFeatures={this.props.enabledFeatures}
      />
    );
  }

  renderPopup = () => {
    const { showingLocations, showingLocationEdit } = this.state;
    const isPopupOpen = showingLocations || showingLocationEdit;
    return (
      <Popup
        id="locationsPopup"
        name="locationsPopup"
        basic
        pinned
        wide
        size="large"
        position="top left"
        open={isPopupOpen}
        onClose={this.onClosePopup}
        context={this.locationInputReference}
      >
        <Popup.Content>
          {this.renderLocations()}
          {this.renderLocationEdit()}
        </Popup.Content>
      </Popup>
    );
  }

  render() {
    return (
      <Fragment>

        <span styleName="virtual-event-checkbox">
          <Checkbox
            data-cy="virtual-event-checkbox"
            id="virtual-event"
            name="virtual-event"
            onChange={(e) => this.props.setVirtual(e.target.checked)}
            checked={this.props.virtual}
            label={{
              htmlFor: 'virtual-event',
              children: (<FormattedMessage {...messages.locationVirtualEventCheck} />),
            }}
          />
        </span>

        {this.renderLocationInput()}
        {this.renderPopup()}

      </Fragment>
    );
  }
}

export default injectIntl(CollegeEventLocationComponent);
